// BAO-125 Phase 5a: the `bh_consent` cookie and what a consent state means.
//
// Two categories (plan §Solution 6):
//   analytics - PostHog + GA traffic measurement. ON by default; the visitor
//               may decline.
//   ads       - GA ad signals, Google Ads tag, Meta pixel, and the server-side
//               conversion sends. OFF until the visitor accepts.
// Global Privacy Control (`navigator.globalPrivacyControl` in the browser,
// `Sec-GPC: 1` on the server) forces `ads: denied` and the banner cannot
// override it.
//
// Region (2026-10-01 amendment to BAO-125 Q1): "OFF until the visitor
// accepts" holds only in the EU/EEA/UK/CH and wherever the country is
// unknown. Everywhere else ads are ON until the visitor declines (opt-out).
// The middleware writes the `bh_region` cookie from Vercel's country header,
// so every reader below gets the region from the same cookie string it
// already parses for `bh_consent`.
//
// Pure module: no DOM, no Next imports. The client hook
// (`lib/analytics/use-consent.ts`), the banner, the pixels, the bootstrap
// script and the consent route all build on it. Phase 2 already reads the
// cookie through `parseConsentCookie` in `attribution.ts`; this module owns
// the WRITER side and the semantics.
import {
  CONSENT_COOKIE,
  CONSENT_RECORD_VERSION,
  parseConsentCookie,
  readCookieValue,
  validateConsentRecord,
  type ConsentSnapshot,
} from './attribution'

export { CONSENT_COOKIE }

/**
 * Bump when the categories or their meaning change: a record written under an
 * older version no longer counts as a decision and the banner re-prompts.
 */
export const CONSENT_VERSION = CONSENT_RECORD_VERSION
/** 13 months, the common regulatory ceiling for a consent record. */
export const CONSENT_COOKIE_MAX_AGE_SECONDS = 13 * 30 * 24 * 60 * 60
/** Stored on `family_attribution.consent_version` so DB rows say which text was consented to. */
export const CONSENT_VERSION_LABEL = `v${CONSENT_VERSION}`

export type ConsentChoice = 'granted' | 'denied'

export interface ConsentRecord {
  analytics: ConsentChoice
  ads: ConsentChoice
  /** ISO-8601 time of the choice. */
  ts: string
  v: typeof CONSENT_VERSION
}

export const DEFAULT_CONSENT: Readonly<Pick<ConsentRecord, 'analytics' | 'ads'>> = {
  analytics: 'granted',
  ads: 'denied',
}

// ---------------------------------------------------------------------------
// Region
// ---------------------------------------------------------------------------

/** Written by the middleware; absent means opt-in (local, CI, unknown country). */
export const REGION_COOKIE = 'bh_region'
export const REGION_COOKIE_MAX_AGE_SECONDS = 30 * 24 * 60 * 60

/** `opt_in`: ads off until accepted. `opt_out`: ads on until declined. */
export type ConsentRegion = 'opt_in' | 'opt_out'

/** EU (27) + EEA (IS, LI, NO) + UK + Switzerland: ads stay opt-in here. */
export const OPT_IN_COUNTRIES: ReadonlySet<string> = new Set([
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE',
  'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
  'IS', 'LI', 'NO',
  'GB',
  'CH',
])

/** Vercel's `x-vercel-ip-country` → region. Unknown country stays opt-in. */
export function regionForCountry(country: string | null | undefined): ConsentRegion {
  const code = country?.trim().toUpperCase()
  if (!code || !/^[A-Z]{2}$/.test(code)) return 'opt_in'
  return OPT_IN_COUNTRIES.has(code) ? 'opt_in' : 'opt_out'
}

/** Anything but an exact `opt_out` is opt-in: the safe reading of a missing or forged value. */
export function readConsentRegion(cookieString: string | null | undefined): ConsentRegion {
  return readCookieValue(cookieString, REGION_COOKIE) === 'opt_out' ? 'opt_out' : 'opt_in'
}

export interface ConsentState {
  /** The stored decision for the current version, or null (no decision yet / stale version). */
  record: ConsentRecord | null
  /** What applies right now: the record or the defaults, with GPC forcing ads denied. */
  effective: { analytics: ConsentChoice; ads: ConsentChoice }
  /** true when a current-version record exists (the banner stays hidden). */
  decided: boolean
  /** true when a Global Privacy Control signal is present. */
  gpc: boolean
  /** Which default applies while undecided (`bh_region`). */
  region: ConsentRegion
}

/** A current-version `bh_consent` record, or null for anything else (shared validator in attribution.ts). */
export function parseConsentRecord(raw: string | null | undefined): ConsentRecord | null {
  const rec = validateConsentRecord(raw)
  if (!rec || rec.v !== CONSENT_VERSION) return null
  return { analytics: rec.analytics, ads: rec.ads, ts: rec.ts, v: CONSENT_VERSION }
}

/**
 * Resolve the state from a cookie string (`document.cookie` or a Cookie
 * header) and a GPC signal.
 *
 * A record written under an OLDER version is not a decision (the banner
 * re-prompts), but its choices stay in force until the visitor answers
 * again - a version bump must never silently re-enable something a visitor
 * switched off.
 */
export function readConsentState(cookieString: string | null | undefined, gpc: boolean): ConsentState {
  const raw = readCookieValue(cookieString, CONSENT_COOKIE)
  const record = parseConsentRecord(raw)
  const stale = record ? null : validateConsentRecord(raw)
  const region = readConsentRegion(cookieString)
  const base = record ?? stale ?? {
    analytics: DEFAULT_CONSENT.analytics,
    ads: region === 'opt_out' ? 'granted' : DEFAULT_CONSENT.ads,
  }
  return {
    record,
    effective: { analytics: base.analytics, ads: gpc ? 'denied' : base.ads },
    decided: record !== null,
    gpc,
    region,
  }
}

/** The Phase 2 snapshot shape (`granted | denied | unset`) for one state. */
export function toConsentSnapshot(state: ConsentState): ConsentSnapshot {
  if (!state.decided) {
    // No decision: analytics runs by default but is not a consent. Opt-in
    // region: ads are off, and `unset` tells the server "never send ad
    // conversions" while letting a later explicit choice overwrite cleanly.
    // Opt-out region: ads are on until declined, so the server sends too.
    // (`readConsentState` owns that default, and a stale record's choice.)
    if (state.gpc) return { analytics: 'unset', ads: 'denied' }
    return { analytics: 'unset', ads: state.region === 'opt_out' ? state.effective.ads : 'unset' }
  }
  return { analytics: state.effective.analytics, ads: state.effective.ads }
}

/** What a visitor explicitly chose; a category left out keeps its current value. */
export type ConsentChoiceInput = { analytics?: ConsentChoice; ads?: ConsentChoice }

/**
 * Build a new record from a choice on top of the current effective state;
 * GPC wins over an ads accept. Untouched categories carry over, so a
 * marketing-only Accept never rewrites an analytics decline.
 */
export function makeConsentRecord(
  choice: ConsentChoiceInput,
  gpc: boolean,
  now: Date = new Date(),
  current: { analytics: ConsentChoice; ads: ConsentChoice } = DEFAULT_CONSENT,
): ConsentRecord {
  const ads = choice.ads ?? current.ads
  return {
    analytics: choice.analytics ?? current.analytics,
    ads: gpc ? 'denied' : ads,
    ts: now.toISOString(),
    v: CONSENT_VERSION,
  }
}

export function encodeConsentCookie(record: ConsentRecord): string {
  return encodeURIComponent(JSON.stringify(record))
}

/** The full `document.cookie` assignment string. */
export function consentCookieString(record: ConsentRecord, secure: boolean): string {
  return (
    `${CONSENT_COOKIE}=${encodeConsentCookie(record)}` +
    `; Max-Age=${CONSENT_COOKIE_MAX_AGE_SECONDS}; Path=/; SameSite=Lax${secure ? '; Secure' : ''}`
  )
}

// ---------------------------------------------------------------------------
// GPC detection
// ---------------------------------------------------------------------------

/** `navigator.globalPrivacyControl` (client). */
export function detectGpc(nav: object | undefined): boolean {
  return (nav as { globalPrivacyControl?: unknown } | undefined)?.globalPrivacyControl === true
}

/** `Sec-GPC: 1` (server). */
export function hasGpcHeader(headers: { get(name: string): string | null }): boolean {
  return headers.get('sec-gpc') === '1'
}

// ---------------------------------------------------------------------------
// Runtime side effects (gtag Consent Mode + PostHog opt-out)
// ---------------------------------------------------------------------------

export interface ConsentRuntime {
  gtag?: (...args: unknown[]) => void
  posthog?: {
    opt_out_capturing: () => void
    opt_in_capturing: () => void
    has_opted_out_capturing: () => boolean
  }
}

/** The Consent Mode v2 update for an effective state. */
export function gtagConsentUpdate(effective: ConsentState['effective']) {
  return {
    analytics_storage: effective.analytics,
    ad_storage: effective.ads,
    ad_user_data: effective.ads,
    ad_personalization: effective.ads,
  }
}

/**
 * Push the effective state into gtag and PostHog. Idempotent: PostHog's
 * opt-out is persisted by posthog-js itself, so this only flips it when it
 * disagrees with the cookie. Never throws.
 */
export function applyConsentSideEffects(effective: ConsentState['effective'], runtime: ConsentRuntime): void {
  try {
    runtime.gtag?.('consent', 'update', gtagConsentUpdate(effective))
  } catch {
    // gtag is third-party code; never let it break the page
  }
  try {
    const ph = runtime.posthog
    if (!ph) return
    const optedOut = ph.has_opted_out_capturing()
    if (effective.analytics === 'denied' && !optedOut) ph.opt_out_capturing()
    if (effective.analytics === 'granted' && optedOut) ph.opt_in_capturing()
  } catch {
    // same for posthog-js
  }
}

/** Convenience for callers that only hold the cookie string. */
export function currentConsentSnapshot(cookieString: string | null | undefined, gpc: boolean): ConsentSnapshot {
  return toConsentSnapshot(readConsentState(cookieString, gpc))
}

/** Re-exported so the Phase 2 reader and this writer stay one shape. */
export { parseConsentCookie }
